import {
    FormValidateOrFn as TanstackFormValidateOrFn,
    FormAsyncValidateOrFn as TanstackFormAsyncValidateOrFn,
    useForm as useTanstackForm,
    useStore as useTanstackStore
} from "@tanstack/react-form";
import { FormApi, FormOptions } from "../shared/types";
import { useModalContext } from "@/components/modal";
import { notifyError, SilentSubmitError } from "@/shared/notification";
import { useMemo } from "react";
import { MAIN_SUBMIT_BUTTON_NAME, useFieldSet } from "../modules/field-set";

export default function useForm<
    TFormData,
    TOnMount extends undefined | TanstackFormValidateOrFn<TFormData>,
    TOnChange extends undefined | TanstackFormValidateOrFn<TFormData>,
    TOnChangeAsync extends undefined | TanstackFormAsyncValidateOrFn<TFormData>,
    TOnBlur extends undefined | TanstackFormValidateOrFn<TFormData>,
    TOnBlurAsync extends undefined | TanstackFormAsyncValidateOrFn<TFormData>,
    TOnSubmit extends undefined | TanstackFormValidateOrFn<TFormData>,
    TOnSubmitAsync extends undefined | TanstackFormAsyncValidateOrFn<TFormData>,
    TOnServer extends undefined | TanstackFormAsyncValidateOrFn<TFormData>,
    TSubmitMeta
>({
    name: receivedName,

    submitOnce = false,
    closeModalOnSubmit = false,
    preventCloseModalWhileSubmitting = true,

    enterBehaviour = "selectNext",
    disabled: receivedDisabled = false,

    parentForm,
    disableWithParent = true,

    onSubmit,
    onSubmitInvalid,

    ...props
}: FormOptions<TFormData, TOnMount, TOnChange, TOnChangeAsync, TOnBlur, TOnBlurAsync, TOnSubmit, TOnSubmitAsync, TOnServer, TSubmitMeta
> = {}): FormApi<TFormData, TOnMount, TOnChange, TOnChangeAsync, TOnBlur, TOnBlurAsync, TOnSubmit, TOnSubmitAsync, TOnServer, TSubmitMeta
> {
    //Modal
    const modal = useModalContext();

    //Form
    const tanstackForm = useTanstackForm({
        ...props,
        onSubmit: async ({ formApi, meta, value }) => {
            if (preventCloseModalWhileSubmitting) modal.setCloseable(false);
            onSubmit && await onSubmit({ formApi: formApi as any, meta, value });
            modal.setCloseable(true);
            if (closeModalOnSubmit) modal.handleClose();
        },
        onSubmitInvalid: ({ formApi, value }) => {
            const mode = import.meta.env.MODE;
            if (mode == "development") console.log(formApi.state.errorMap)
            onSubmitInvalid && onSubmitInvalid({ formApi: formApi as any, value })
        },
    })

    //FieldSet
    const [isSubmitting, isSubmitSuccessful] = useTanstackStore(tanstackForm.store, state => [state.isSubmitting, state.isSubmitSuccessful]);
    const disabled = receivedDisabled || (disableWithParent && parentForm?.disabled) || isSubmitting || (submitOnce && isSubmitSuccessful);
    const fieldSet = useFieldSet({
        name: receivedName,
        disabled,
        parentFieldSet: parentForm?.fieldSet,
        disableWithParent,
        onSubmit: async e => {
            try {
                await tanstackForm.handleSubmit(e.submitter as TSubmitMeta)
            } catch (e) {
                if (!(e instanceof SilentSubmitError))
                    notifyError(e);
                modal.setCloseable(true);
            }
        },
    });

    return useMemo(() => ({
        //Override fields
        ...tanstackForm,

        //Don't know why it doesn't override
        handleSubmit: tanstackForm.handleSubmit,
        state: tanstackForm.state,
        runValidator: tanstackForm.runValidator,
        setErrorMap: tanstackForm.setErrorMap,

        // Override setFieldValue to clear onSubmit errors reactively
        setFieldValue: (name: any, value: any, opts?: any) => {
            tanstackForm.setFieldValue(name, value, opts);
            const fieldMeta = tanstackForm.getFieldMeta(name);
            if ((fieldMeta?.errorMap as any)?.onSubmit) {
                tanstackForm.setFieldMeta(name, (prev: any) => ({
                    ...prev,
                    errorMap: { ...prev.errorMap, onSubmit: undefined }
                }));
            }
            if ((tanstackForm.state.errorMap as any)?.onSubmit) {
                tanstackForm.setErrorMap({ onSubmit: undefined } as any);
            }
        },

        //Added fields
        parentForm,
        fieldSet,
        disabled,
        enterBehaviour,
        focusField: (name: any) => fieldSet.focusFieldByName(name),
        focusSubmitButton: (name: string = MAIN_SUBMIT_BUTTON_NAME) => fieldSet.focusSubmitButtonByName(name)
    }), [tanstackForm, parentForm, fieldSet, disabled, enterBehaviour])
}